import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import { env } from "@/config/env";
import { keycloak } from "@/auth/keycloak";

/**
 * Single axios instance used by every API module.
 *
 * Before each request: if a Keycloak session exists, refresh the access
 * token when it's within 30 s of expiry, then attach as a Bearer header.
 * The gateway validates the JWT and forwards X-User-* headers downstream.
 */
const axiosClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 20_000,
});

axiosClient.interceptors.request.use(async (config) => {
  if (keycloak.authenticated) {
    try {
      await keycloak.updateToken(30);
    } catch {
      // token irrecoverable — caller will see 401
    }
    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
  }
  return config;
});

axiosClient.interceptors.response.use(
  (resp) => resp,
  (err: AxiosError) => {
    if (err.response?.status === 401 && keycloak.authenticated) {
      keycloak.login();
    }
    return Promise.reject(err);
  },
);

export default axiosClient;

export async function apiGet<T>(url: string, config?: AxiosRequestConfig) {
  const { data } = await axiosClient.get<T>(url, config);
  return data;
}
export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
) {
  const { data } = await axiosClient.post<T>(url, body, config);
  return data;
}
export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
) {
  const { data } = await axiosClient.put<T>(url, body, config);
  return data;
}
export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
) {
  const { data } = await axiosClient.patch<T>(url, body, config);
  return data;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig) {
  const { data } = await axiosClient.delete<T>(url, config);
  return data;
}
